import { useState, useMemo, useEffect, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DropDown from "@components/ui/dropdown/DropDown";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { bookingsApi } from "@services/api/bookings";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { useDataStore } from "@store/user_org";

// ─── Types ──────────────────────────────────────────────────

interface BookingData {
  immatriculation: string;
  km: string;
  service: string;
  intervention_type: string;
  intervention: string;
  mobility: string;
  tire_option: string;
  deposit_date: string | null;
  deposit_time: string;
  restitution_date: string | null;
  restitution_time: string;
  phone: string;
  civilite: string;
  nom: string;
  prenom: string;
  email: string;
  code_postal: string;
  comments: string;
  channel: string;
  privacy_accepted: boolean;
  marketing_accepted: boolean;
}

const INITIAL: BookingData = {
  immatriculation: "",
  km: "",
  service: "",
  intervention_type: "",
  intervention: "",
  mobility: "",
  tire_option: "",
  deposit_date: null,
  deposit_time: "",
  restitution_date: null,
  restitution_time: "",
  phone: "",
  civilite: "",
  nom: "",
  prenom: "",
  email: "",
  code_postal: "",
  comments: "",
  channel: "whatsapp",
  privacy_accepted: false,
  marketing_accepted: false,
};

const SERVICES = [
  { value: "mecanique", label: "Mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "vitrage", label: "Vitrage" },
  { value: "pneumatique", label: "Pneumatique" },
];
const INTERVENTION_TYPES = [
  { value: "entretien", label: "Entretien" },
  { value: "reparation", label: "Réparation" },
  { value: "diagnostic", label: "Diagnostic" },
  { value: "controle_technique", label: "Contrôle technique" },
];
// Interventions are now fetched from the API — see main widget component
const MOBILITY_OPTIONS = [
  { value: "wait", label: "Je peux attendre sur place (1h30)" },
  { value: "bike", label: "Demander un vélo équipé (gratuit)" },
  { value: "loan", label: "Demander un véhicule de prêt (zone limitée)" },
  { value: "rental", label: "Demander un véhicule de location (liberté/payant)" },
];
const TIRE_OPTIONS = [
  { value: "budget", label: "Pneus premier prix" },
  { value: "mid", label: "Pneus intermédiaires" },
  { value: "premium", label: "Pneus premium" },
];

const MORNING_SLOTS = ["08:00", "08:15", "08:30", "08:45", "09:00", "09:15", "09:30", "09:45", "10:00", "10:15", "10:30", "10:45", "11:00"];
const AFTERNOON_SLOTS = ["14:00", "14:15", "14:30", "14:45", "15:00", "15:15", "15:30", "15:45", "16:00", "16:15", "16:30", "16:45", "17:00", "17:15", "17:30", "17:45", "18:00"];

const STEP_TITLES = ["Informations véhicule", "Date de dépôt", "Date de restitution", "Coordonnées"];
const STEP_COUNT = 4;

// ─── Small reusable pieces ──────────────────────────────────

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="flex flex-row items-center gap-[5px]">
      {Array.from({ length: STEP_COUNT }).map((_, i) => (
        <div
          key={i}
          className="h-1 flex-1 rounded-[5px]"
          style={{ background: i === step ? "#5C6BFF" : "#D9D9D9" }}
        />
      ))}
    </div>
  );
}

// ─── Calendar (widget-specific, no existing equivalent) ─────

const DAYS_HEADER = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

function MiniCalendar({
  selectedDate,
  onSelect,
  minDate,
  fullDays,
  onMonthChange,
}: {
  selectedDate: string | null;
  onSelect: (date: string) => void;
  minDate?: string;
  fullDays?: string[];
  onMonthChange?: (monthStr: string) => void;
}) {
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const changeMonth = (next: { year: number; month: number }) => {
    setViewMonth(next);
    onMonthChange?.(`${next.year}-${String(next.month + 1).padStart(2, "0")}`);
  };

  const monthLabel = useMemo(() => {
    const d = new Date(viewMonth.year, viewMonth.month, 1);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [viewMonth]);

  const weeks = useMemo(() => {
    const first = new Date(viewMonth.year, viewMonth.month, 1);
    const lastDay = new Date(viewMonth.year, viewMonth.month + 1, 0).getDate();
    let startDow = first.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;

    const cells: { day: number; inMonth: boolean; dateStr: string }[] = [];
    const prevMonthLast = new Date(viewMonth.year, viewMonth.month, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
      const d = prevMonthLast - i;
      const m = viewMonth.month === 0 ? 11 : viewMonth.month - 1;
      const y = viewMonth.month === 0 ? viewMonth.year - 1 : viewMonth.year;
      cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    for (let d = 1; d <= lastDay; d++) {
      cells.push({ day: d, inMonth: true, dateStr: `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const m = viewMonth.month === 11 ? 0 : viewMonth.month + 1;
        const y = viewMonth.month === 11 ? viewMonth.year + 1 : viewMonth.year;
        cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
      }
    }

    const rows: typeof cells[] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [viewMonth]);

  const cutoff = minDate || new Date().toISOString().slice(0, 10);
  const fullSet = useMemo(() => new Set(fullDays || []), [fullDays]);
  const isPast = (dateStr: string) => dateStr < cutoff;
  const isFull = (dateStr: string) => fullSet.has(dateStr);

  return (
    <div className="flex flex-col gap-[10px]" style={{ width: "100%" }}>
      <div className="flex flex-row items-center justify-between px-[10px]">
        <button onClick={() => changeMonth(viewMonth.month === 0 ? { year: viewMonth.year - 1, month: 11 } : { year: viewMonth.year, month: viewMonth.month - 1 })}>
          <svg width={5} height={10}><path d="M5 0 L0 5 L5 10" fill="none" stroke="#A1A1A1" strokeWidth={1} /></svg>
        </button>
        <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#A1A1A1", textTransform: "capitalize" }}>
          {monthLabel}
        </span>
        <button onClick={() => changeMonth(viewMonth.month === 11 ? { year: viewMonth.year + 1, month: 0 } : { year: viewMonth.year, month: viewMonth.month + 1 })}>
          <svg width={5} height={10}><path d="M0 0 L5 5 L0 10" fill="none" stroke="#A1A1A1" strokeWidth={1} /></svg>
        </button>
      </div>

      <div className="flex flex-row justify-between px-[10px]">
        {DAYS_HEADER.map((d) => (
          <span key={d} style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#A1A1A1", width: 25, textAlign: "center" }}>
            {d}
          </span>
        ))}
      </div>

      <div className="flex flex-col gap-[5px]">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-row justify-between px-[10px]">
            {week.map((cell) => {
              const isSelected = selectedDate === cell.dateStr;
              const full = cell.inMonth && !isPast(cell.dateStr) && isFull(cell.dateStr);
              const disabled = !cell.inMonth || isPast(cell.dateStr) || full;
              return (
                <button
                  key={cell.dateStr}
                  className="flex items-center justify-center relative"
                  style={{
                    width: 25,
                    height: 25,
                    borderRadius: 30,
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: 12,
                    lineHeight: "18px",
                    color: isSelected ? "#FFFFFF" : disabled ? "#D9D9D9" : "#A1A1A1",
                    background: isSelected ? "#000000" : "transparent",
                    textDecoration: full ? "line-through" : "none",
                    boxShadow: isSelected ? "0px 2px 4px rgba(0, 0, 0, 0.15)" : "none",
                    cursor: disabled ? "default" : "pointer",
                  }}
                  onClick={() => !disabled && onSelect(cell.dateStr)}
                  disabled={disabled}
                  title={full ? "Journée complète" : undefined}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Time slot picker (widget-specific, no existing equivalent) ─

function TimeSlots({
  selected,
  onSelect,
  disabledSlots,
}: {
  selected: string;
  onSelect: (v: string) => void;
  disabledSlots?: string[];
}) {
  const disabled = disabledSlots || [];
  return (
    <div className="flex flex-col gap-[2px]">
      <span className="text-h4 text-neutral-400">Matin</span>
      <div className="flex flex-row flex-wrap gap-[2px]">
        {MORNING_SLOTS.map((slot) => {
          const isDisabled = disabled.includes(slot);
          const isSelected = selected === slot;
          return (
            <button
              key={slot}
              className="flex items-center rounded-[10px] border px-[10px] py-[8px] text-h3"
              style={{
                borderColor: "#D9D9D9",
                background: isSelected ? "#000000" : isDisabled ? "#EEEEEE" : "#FFFFFF",
                color: isSelected ? "#FFFFFF" : isDisabled ? "#A1A1A1" : "#000000",
                cursor: isDisabled ? "default" : "pointer",
              }}
              onClick={() => !isDisabled && onSelect(slot)}
              disabled={isDisabled}
            >
              {slot}
            </button>
          );
        })}
      </div>

      <span className="text-h4 text-neutral-400 mt-1">Après-midi</span>
      <div className="flex flex-row flex-wrap gap-[2px]">
        {AFTERNOON_SLOTS.map((slot) => {
          const isDisabled = disabled.includes(slot);
          const isSelected = selected === slot;
          return (
            <button
              key={slot}
              className="flex items-center rounded-[10px] border px-[10px] py-[8px] text-h3"
              style={{
                borderColor: "#D9D9D9",
                background: isSelected ? "#000000" : isDisabled ? "#EEEEEE" : "#FFFFFF",
                color: isSelected ? "#FFFFFF" : isDisabled ? "#A1A1A1" : "#000000",
                cursor: isDisabled ? "default" : "pointer",
              }}
              onClick={() => !isDisabled && onSelect(slot)}
              disabled={isDisabled}
            >
              {slot}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step components ────────────────────────────────────────

function Step1({ data, update, orgId, interventions }: { data: BookingData; update: (k: keyof BookingData, v: any) => void; orgId: string; interventions: Intervention[] }) {
  // Filter type options based on which types have active interventions in the selected category
  const availableTypes = useMemo(() => {
    if (!data.service) return INTERVENTION_TYPES;
    const typesInCategory = new Set(
      interventions.filter((i) => i.active && i.category === data.service).map((i) => i.intervention_type),
    );
    return INTERVENTION_TYPES.filter((t) => typesInCategory.has(t.value));
  }, [data.service, interventions]);

  // Filter interventions by selected category + type
  const interventionOptions = useMemo(() => {
    let list = interventions.filter((i) => i.active);
    if (data.service) list = list.filter((i) => i.category === data.service);
    if (data.intervention_type) list = list.filter((i) => i.intervention_type === data.intervention_type);
    return list.map((i) => ({ value: i.name, label: i.name }));
  }, [interventions, data.service, data.intervention_type]);
  const [vehicleLookup, setVehicleLookup] = useState<{ found: boolean; marque?: string; modele?: string; image_url?: string } | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const plate = data.immatriculation.replace(/[\s-]/g, "");
    if (plate.length < 7) {
      setVehicleLookup(null);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const result = await bookingsApi.lookupVehicle(data.immatriculation, orgId);
        setVehicleLookup(result);
      } catch {
        setVehicleLookup(null);
      }
      setLookingUp(false);
    }, 500);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.immatriculation, orgId]);

  return (
    <div className="flex flex-col gap-[5px]">
      {/* Immatriculation with vehicle lookup */}
      <div className="flex flex-col gap-[2px]">
        <TextField
          label="IMMATRICULATION"
          value={data.immatriculation}
          onChange={(v: string) => update("immatriculation", v.toUpperCase())}
          placeholder="AA-123-BB"
        />
        {lookingUp && (
          <div className="flex items-center px-[10px] py-[8px] rounded-b-[10px]" style={{ background: "#F5F5F5", border: "1px solid #D9D9D9" }}>
            <span className="text-h3 text-neutral-400">Recherche...</span>
          </div>
        )}
        {!lookingUp && vehicleLookup?.found && (
          <div className="flex items-center px-[10px] py-[8px] rounded-b-[10px]" style={{ background: "#E8EAFF", border: "1px solid #818CF8" }}>
            <span className="text-h3" style={{ color: "#5C6BFF" }}>
              Véhicule identifié — {vehicleLookup.marque} {vehicleLookup.modele}
            </span>
          </div>
        )}
        {!lookingUp && vehicleLookup && !vehicleLookup.found && data.immatriculation.replace(/[\s-]/g, "").length >= 7 && (
          <div className="flex items-center px-[10px] py-[8px] rounded-b-[10px]" style={{ background: "#FFF7ED", border: "1px solid #FDBA74" }}>
            <span className="text-h3" style={{ color: "#EA580C" }}>
              Véhicule non reconnu
            </span>
          </div>
        )}
        {!lookingUp && vehicleLookup?.found && vehicleLookup.image_url && (
          <div className="flex items-center justify-center px-[10px] py-[8px]">
            <img src={vehicleLookup.image_url} alt="vehicle" className="object-contain" style={{ height: 55 }} />
          </div>
        )}
      </div>

      <TextField label="Kilométrage" value={data.km} onChange={(v: string) => update("km", v)} placeholder="ex: 45000" />

      <DropDown label="Service" value={data.service} options={SERVICES} onSelect={(v) => { update("service", v); update("intervention_type", ""); update("intervention", ""); }} placeholder="Sélectionner" />

      {data.service && (
        <DropDown label="Type d'intervention" value={data.intervention_type} options={availableTypes} onSelect={(v) => { update("intervention_type", v); update("intervention", ""); }} placeholder="Sélectionner" />
      )}

      {/* Interventions with estimated duration */}
      {data.service && (
      <div className="flex flex-col gap-[2px]">
        <DropDown label="Intervention" value={data.intervention} options={interventionOptions} onSelect={(v) => update("intervention", v)} placeholder="Sélectionner" />
        {data.intervention && (() => {
          const intv = interventions.find((i) => i.name === data.intervention);
          const minutes = intv?.estimated_minutes ?? 90;
          const h = Math.floor(minutes / 60);
          const m = minutes % 60;
          const label = h > 0 && m > 0 ? `${h}h${m.toString().padStart(2, "0")}` : h > 0 ? `${h}h` : `${m}min`;
          return (
            <div className="flex items-center px-[10px] py-[8px] rounded-b-[10px]" style={{ background: "#E8EAFF", border: "1px solid #818CF8" }}>
              <span className="text-h3" style={{ color: "#5C6BFF" }}>
                Durée estimée {label}
              </span>
            </div>
          );
        })()}
      </div>
      )}

      {/* Mobility options */}
      <div className="flex flex-col gap-[2px] pt-[10px] px-[10px]">
        {MOBILITY_OPTIONS.map((opt) => (
          <CheckBoxInput
            key={opt.value}
            checked={data.mobility === opt.value}
            label={opt.label}
            onChange={() => update("mobility", data.mobility === opt.value ? "" : opt.value)}
          />
        ))}
      </div>

      {/* Tire options */}
      <div className="flex flex-col gap-[2px] pt-[10px] px-[10px]">
        {TIRE_OPTIONS.map((opt) => (
          <CheckBoxInput
            key={opt.value}
            checked={data.tire_option === opt.value}
            label={opt.label}
            onChange={() => update("tire_option", data.tire_option === opt.value ? "" : opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

function Step2({ data, update, orgId, minDaysAhead }: { data: BookingData; update: (k: keyof BookingData, v: any) => void; orgId: string; minDaysAhead: number }) {
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const [fullDays, setFullDays] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const allSlots = useMemo(() => [...MORNING_SLOTS, ...AFTERNOON_SLOTS], []);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + minDaysAhead);
    return d.toISOString().slice(0, 10);
  }, [minDaysAhead]);

  const currentMonthStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const fetchFullDays = (month: string) => {
    if (!orgId) return;
    bookingsApi.getFullDays(month, orgId)
      .then((res) => setFullDays((prev) => [...(prev || []).filter((d) => !d.startsWith(month)), ...res.full_days]))
      .catch(() => setFullDays((prev) => prev ?? []));
  };

  // Fetch full days for current month on mount
  useEffect(() => { fetchFullDays(currentMonthStr); }, [orgId]);

  // Auto-select first available day after fullDays are loaded
  useEffect(() => {
    if (data.deposit_date) return;
    if (fullDays === null) return; // wait for fetch to complete
    const fullSet = new Set(fullDays);
    const d = new Date();
    d.setDate(d.getDate() + minDaysAhead);
    // Skip weekends and full days (max 60 days lookahead)
    for (let i = 0; i < 60; i++) {
      if (d.getDay() !== 0 && d.getDay() !== 6 && !fullSet.has(d.toISOString().slice(0, 10))) break;
      d.setDate(d.getDate() + 1);
    }
    update("deposit_date", d.toISOString().slice(0, 10));
  }, [fullDays]);

  useEffect(() => {
    if (!data.deposit_date || !orgId) { setUnavailable([]); return; }
    setLoading(true);
    bookingsApi.getAvailability(data.deposit_date, data.service || "Mécanique", orgId)
      .then((res) => {
        setUnavailable(res.unavailable_slots);
        // Auto-select first available slot
        const firstAvailable = allSlots.find((s) => !res.unavailable_slots.includes(s));
        if (firstAvailable && !data.deposit_time) update("deposit_time", firstAvailable);
      })
      .catch(() => setUnavailable([]))
      .finally(() => setLoading(false));
  }, [data.deposit_date, data.service, orgId]);

  return (
    <div className="flex flex-col gap-[20px]">
      <MiniCalendar selectedDate={data.deposit_date} onSelect={(d) => { update("deposit_date", d); update("deposit_time", ""); }} minDate={minDate} fullDays={fullDays || []} onMonthChange={fetchFullDays} />
      {data.deposit_date && (
        loading ? (
          <span className="text-h3 text-neutral-400 px-[10px]">Chargement des créneaux...</span>
        ) : (
          <TimeSlots selected={data.deposit_time} onSelect={(t) => update("deposit_time", t)} disabledSlots={unavailable} />
        )
      )}
    </div>
  );
}

function Step3({ data, update, orgId, minDaysAhead, safetyMarginHours, interventions }: { data: BookingData; update: (k: keyof BookingData, v: any) => void; orgId: string; minDaysAhead: number; safetyMarginHours: number; interventions: Intervention[] }) {
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const [fullDays, setFullDays] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const allSlots = useMemo(() => [...MORNING_SLOTS, ...AFTERNOON_SLOTS], []);

  // Get the selected intervention's data
  const selectedIntervention = useMemo(() => interventions.find((i) => i.name === data.intervention), [interventions, data.intervention]);
  const interventionMinDays = selectedIntervention?.min_days_margin ?? 0;

  // Compute earliest restitution slot based on deposit time + intervention duration + safety margin
  const tooEarlySlots = useMemo(() => {
    if (!data.deposit_time || !data.deposit_date || data.restitution_date !== data.deposit_date) return [];
    const interventionMinutes = selectedIntervention?.estimated_minutes ?? 90;
    const interventionHours = interventionMinutes / 60;
    const [hh, mm] = data.deposit_time.split(":").map(Number);
    const depositMinutes = hh * 60 + mm;
    const earliestMinutes = depositMinutes + (interventionHours + safetyMarginHours) * 60;
    return allSlots.filter((slot) => {
      const [sh, sm] = slot.split(":").map(Number);
      return sh * 60 + sm < earliestMinutes;
    });
  }, [data.deposit_time, data.deposit_date, data.restitution_date, selectedIntervention, safetyMarginHours, allSlots]);

  // Restitution min date = deposit date + intervention margin days
  const minDate = useMemo(() => {
    if (data.deposit_date) {
      const d = new Date(data.deposit_date + "T12:00:00");
      d.setDate(d.getDate() + interventionMinDays);
      return d.toISOString().slice(0, 10);
    }
    const d = new Date();
    d.setDate(d.getDate() + minDaysAhead);
    return d.toISOString().slice(0, 10);
  }, [minDaysAhead, data.deposit_date, interventionMinDays]);

  const currentMonthStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const fetchFullDays = (month: string) => {
    if (!orgId) return;
    bookingsApi.getFullDays(month, orgId)
      .then((res) => setFullDays((prev) => [...(prev || []).filter((d) => !d.startsWith(month)), ...res.full_days]))
      .catch(() => setFullDays((prev) => prev ?? []));
  };

  useEffect(() => { fetchFullDays(currentMonthStr); }, [orgId]);

  // Auto-select first available day after fullDays are loaded
  useEffect(() => {
    if (data.restitution_date) return;
    if (fullDays === null) return; // wait for fetch to complete
    const fullSet = new Set(fullDays);
    const base = data.deposit_date || minDate;
    const d = new Date(base + "T12:00:00");
    for (let i = 0; i < 60; i++) {
      if (d.getDay() !== 0 && d.getDay() !== 6 && !fullSet.has(d.toISOString().slice(0, 10))) break;
      d.setDate(d.getDate() + 1);
    }
    update("restitution_date", d.toISOString().slice(0, 10));
  }, [fullDays]);

  useEffect(() => {
    if (!data.restitution_date || !orgId) { setUnavailable([]); return; }
    setLoading(true);
    bookingsApi.getAvailability(data.restitution_date, data.service || "Mécanique", orgId)
      .then((res) => {
        setUnavailable(res.unavailable_slots);
      })
      .catch(() => setUnavailable([]))
      .finally(() => setLoading(false));
  }, [data.restitution_date, data.service, orgId]);

  // Merge both disabled lists
  const allDisabled = useMemo(() => {
    const set = new Set([...unavailable, ...tooEarlySlots]);
    return Array.from(set);
  }, [unavailable, tooEarlySlots]);

  // Auto-select first available slot when disabled lists change
  useEffect(() => {
    if (!data.restitution_date || loading) return;
    if (data.restitution_time && !allDisabled.includes(data.restitution_time)) return;
    const firstAvailable = allSlots.find((s) => !allDisabled.includes(s));
    if (firstAvailable) update("restitution_time", firstAvailable);
  }, [allDisabled, loading]);

  return (
    <div className="flex flex-col gap-[20px]">
      <MiniCalendar selectedDate={data.restitution_date} onSelect={(d) => { update("restitution_date", d); update("restitution_time", ""); }} minDate={minDate} fullDays={fullDays || []} onMonthChange={fetchFullDays} />
      {data.restitution_date && (
        loading ? (
          <span className="text-h3 text-neutral-400 px-[10px]">Chargement des créneaux...</span>
        ) : (
          <TimeSlots selected={data.restitution_time} onSelect={(t) => update("restitution_time", t)} disabledSlots={allDisabled} />
        )
      )}
    </div>
  );
}

function Step4({ data, update, orgId }: { data: BookingData; update: (k: keyof BookingData, v: any) => void; orgId: string }) {
  const [clientLookup, setClientLookup] = useState<{ found: boolean; nom?: string; prenom?: string; email?: string } | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const phone = data.phone.replace(/\s/g, "");
    if (phone.length < 10) {
      setClientLookup(null);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const result = await bookingsApi.lookupClient(data.phone, orgId);
        setClientLookup(result);
        if (result.found) {
          if (result.nom) update("nom", result.nom);
          if (result.prenom) update("prenom", result.prenom);
          if (result.email) update("email", result.email);
        }
      } catch {
        setClientLookup(null);
      }
      setLookingUp(false);
    }, 500);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.phone, orgId]);

  const phoneRecognized = clientLookup?.found === true;

  return (
    <div className="flex flex-col gap-[5px]">
      {/* Téléphone with recognition */}
      <div className="flex flex-col gap-[2px]">
        <TextField
          label="Téléphone"
          value={data.phone}
          onChange={(v: string) => update("phone", v)}
          placeholder="06 12 34 56 78"
          type="phone"
        />
        {lookingUp && (
          <div className="flex items-center px-[10px] py-[8px] rounded-b-[10px]" style={{ background: "#F5F5F5", border: "1px solid #D9D9D9" }}>
            <span className="text-h3 text-neutral-400">Recherche...</span>
          </div>
        )}
        {!lookingUp && phoneRecognized && (
          <div className="flex items-center px-[10px] py-[8px] rounded-b-[10px]" style={{ background: "#E8EAFF", border: "1px solid #818CF8" }}>
            <span className="text-h3" style={{ color: "#5C6BFF" }}>
              Bonjour {clientLookup.prenom || clientLookup.nom}, heureux de vous revoir !
            </span>
          </div>
        )}
        {!lookingUp && clientLookup && !clientLookup.found && data.phone.replace(/\s/g, "").length >= 10 && (
          <div className="flex items-center px-[10px] py-[8px] rounded-b-[10px]" style={{ background: "#FFF7ED", border: "1px solid #FDBA74" }}>
            <span className="text-h3" style={{ color: "#EA580C" }}>
              Nouveau client
            </span>
          </div>
        )}
      </div>

      {/* Civilité / Nom / Prénom / Email / Code Postal — dimmed when phone recognized */}
      <div className="flex flex-col gap-[5px]" style={{ opacity: phoneRecognized ? 0.3 : 1 }}>
        <DropDown
          label="Civilité"
          value={data.civilite}
          options={[
            { value: "M.", label: "M." },
            { value: "Mme", label: "Mme" },
            { value: "Société", label: "Société" },
          ]}
          onSelect={(v) => update("civilite", v)}
          placeholder="Sélectionner"
        />
        <TextField label="Nom" value={data.nom} onChange={(v: string) => update("nom", v)} />
        <TextField label="Prénom" value={data.prenom} onChange={(v: string) => update("prenom", v)} />
        <TextField label="E-mail" value={data.email} onChange={(v: string) => update("email", v)} type="email" />
        <TextField label="Code Postal" value={data.code_postal} onChange={(v: string) => update("code_postal", v)} />
      </div>

      {/* Commentaires */}
      <AreaTextField label="Commentaires" value={data.comments} onChange={(v: string) => update("comments", v)} rows={3} />

      {/* Canal de communication */}
      <div className="flex flex-col gap-[2px]">
        <span className="text-h4 text-neutral-400">Canal de communication</span>
        <div className="flex flex-row flex-wrap gap-[2px]">
          {[
            { value: "whatsapp", label: "Whatsapp", icon: "whatsapp", activeColor: "#25D366" },
            { value: "sms", label: "Sms", icon: "send", activeColor: null },
            { value: "email", label: "Email", icon: "email", activeColor: null },
          ].map((ch) => {
            const isActive = data.channel === ch.value;
            return (
              <button
                key={ch.value}
                className="flex flex-row items-center gap-[10px] border rounded-[10px] px-[10px] py-[8px]"
                style={{
                  borderColor: isActive && ch.activeColor ? ch.activeColor : "#D9D9D9",
                  background: "#FFFFFF",
                }}
                onClick={() => update("channel", ch.value)}
              >
                <span className="text-h3" style={{ color: isActive ? "#000000" : "#D9D9D9" }}>
                  {ch.label}
                </span>
                {ch.value === "whatsapp" ? (
                  <svg width={20} height={20} viewBox="0 0 24 24" fill={isActive && ch.activeColor ? ch.activeColor : "#D9D9D9"}>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                ) : (
                  <Icon1 name={ch.icon} size={20} color={isActive && ch.activeColor ? ch.activeColor : "#D9D9D9"} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Checkboxes */}
      <div className="flex flex-col gap-[5px] pt-[10px]">
        <CheckBoxInput
          checked={data.privacy_accepted}
          label="J'ai lu et j'accepte la Politique de confidentialité"
          onChange={() => update("privacy_accepted", !data.privacy_accepted)}
        />
        <CheckBoxInput
          checked={data.marketing_accepted}
          label="J'accepte que mes données soient utilisées à des fins de marketing"
          onChange={() => update("marketing_accepted", !data.marketing_accepted)}
        />
      </div>
    </div>
  );
}

// ─── Confirmation screen ────────────────────────────────────

function ConfirmationScreen({ data }: { data: BookingData }) {
  const formatDate = (d: string | null) => {
    if (!d) return "—";
    const parts = d.split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const rows = [
    { label: "Demande", value: "#57845161" },
    { label: "Véhicule", value: data.immatriculation || "—" },
    { label: "Service", value: SERVICES.find((s) => s.value === data.service)?.label || data.service || "—" },
    { label: "Type", value: INTERVENTION_TYPES.find((t) => t.value === data.intervention_type)?.label || data.intervention_type || "—" },
    { label: "Interventions", value: data.intervention || "—" },
    { label: "Date de dépôt", value: data.deposit_date ? `${formatDate(data.deposit_date)} ${data.deposit_time}` : "—" },
    { label: "Date de restitution", value: data.restitution_date ? `${formatDate(data.restitution_date)} ${data.restitution_time}` : "—" },
  ];

  return (
    <div className="flex flex-col items-center gap-[20px] p-[20px]">
      {/* Success icon */}
      <div className="flex flex-col items-center gap-[10px] px-[20px]">
        <div className="flex items-center justify-center rounded-full" style={{ width: 48, height: 48, background: "#E3FEFC" }}>
          <Icon1 name="done2" size={28} color="#5EEAD4" />
        </div>
        <span className="text-h1 font-bold text-center text-black">
          Demande de RDV enregistrée avec succès
        </span>
      </div>

      {/* Summary card */}
      <div className="flex flex-col w-full border rounded-[15px] py-[10px] gap-[5px]" style={{ borderColor: "#E6E6E6", background: "#FBFCFF" }}>
        {rows.map((row, i) => (
          <div key={row.label}>
            <div className="flex flex-row items-center justify-between px-[10px]">
              <span className="text-h3 text-neutral-300">{row.label}</span>
              <span className="text-h3 text-neutral-400">{row.value}</span>
            </div>
            {i < rows.length - 1 && <div className="mx-0 my-[5px]" style={{ borderTop: "1px solid #D9D9D9" }} />}
          </div>
        ))}
      </div>

      {/* Track button */}
      <Button label="Suivre ma demande" />
    </div>
  );
}

// ─── Main Widget ────────────────────────────────────────────

export default function BookingWidget({ onClose, onSuccess }: { onClose: () => void; onSuccess?: () => void }) {
  const orgData = useDataStore((s) => s.orgData);
  const [step, setStep] = useState(0);
  const [data, setData] = useState<BookingData>({ ...INITIAL });
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interventions, setInterventions] = useState<Intervention[]>([]);

  // Fetch interventions from API on mount
  useEffect(() => {
    if (!orgData?.id) return;
    interventionsApi.list(orgData.id)
      .then((data) => setInterventions(data))
      .catch(() => {});
  }, [orgData?.id]);

  const update = (key: keyof BookingData, value: any) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = async () => {
    if (step < STEP_COUNT - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      try {
        await bookingsApi.create({
          org_id: orgData?.id || "",
          plate: data.immatriculation,
          km: parseInt(data.km) || 0,
          service_type: data.service,
          intervention_type: data.intervention_type,
          interventions: data.intervention ? [data.intervention] : [],
          mobility_option: data.mobility,
          tire_option: data.tire_option,
          deposit_date: data.deposit_date || "",
          deposit_time: data.deposit_time,
          restitution_date: data.restitution_date || "",
          restitution_time: data.restitution_time,
          civilite: data.civilite || undefined,
          nom: data.nom,
          prenom: data.prenom,
          phone: data.phone,
          email: data.email || undefined,
          code_postal: data.code_postal || undefined,
          comments: data.comments || undefined,
          communication_channel: data.channel,
        });
      } catch (e) {
        console.error("Error submitting booking:", e);
      }
      setSubmitting(false);
      setConfirmed(true);
      onSuccess?.();
    }
  };

  if (confirmed) {
    return (
      <div className="flex flex-col border rounded-[15px] overflow-hidden shadow-xl" style={{ width: 380, background: "#FFFFFF", borderColor: "#D9D9D9" }}>
        <div className="flex justify-end p-2">
          <button onClick={onClose} className="hover:bg-neutral-100 rounded-full p-1">
            <Icon1 name="close" size={12} color="#A1A1A1" />
          </button>
        </div>
        <ConfirmationScreen data={data} />
      </div>
    );
  }

  return (
    <div className="flex flex-col border rounded-[15px] overflow-hidden shadow-xl" style={{ width: 380, background: "#FFFFFF", borderColor: "#D9D9D9", padding: 20, gap: 20 }}>
      {/* Header */}
      <div className="flex flex-col gap-[10px]">
        <div className="flex flex-row items-start justify-between">
          <div className="flex flex-col">
            <span className="text-h3 text-neutral-400">Rendez-vous en ligne</span>
            <span className="text-h2 font-medium" style={{ color: "#5C6BFF" }}>
              {STEP_TITLES[step]}
            </span>
          </div>
          <button onClick={onClose} className="hover:bg-neutral-100 rounded-full p-1">
            <Icon1 name="close" size={12} color="#A1A1A1" />
          </button>
        </div>
        <StepIndicator step={step} />
      </div>

      {/* Content */}
      <div className="flex flex-col overflow-y-auto" style={{ maxHeight: 500 }}>
        {step === 0 && <Step1 data={data} update={update} orgId={orgData?.id || ""} interventions={interventions} />}
        {step === 1 && <Step2 data={data} update={update} orgId={orgData?.id || ""} minDaysAhead={orgData?.config?.booking_min_days_ahead ?? 1} />}
        {step === 2 && <Step3 data={data} update={update} orgId={orgData?.id || ""} minDaysAhead={orgData?.config?.booking_min_days_ahead ?? 1} safetyMarginHours={orgData?.config?.booking_safety_margin_hours ?? 1} interventions={interventions} />}
        {step === 3 && <Step4 data={data} update={update} orgId={orgData?.id || ""} />}
      </div>

      {/* Footer */}
      <div className="flex flex-row items-center gap-2">
        {step > 0 && (
          <SecondaryButton label="Retour" onClick={() => setStep(step - 1)} variant="small" />
        )}
        <Button
          label={submitting ? "Envoi..." : step === STEP_COUNT - 1 ? "Confirmer" : "Suivant"}
          onClick={handleNext}
          variant="small"
          disabled={submitting}
        />
      </div>
    </div>
  );
}
